import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import config from '../../../../../../config';
import {
  Estimate,
  EstimateSummary,
  VendorOrder,
  VendorOrderSummary,
} from '../../../../../../app/shared/models/vendor-order.model';
import { SendMessageRequest } from '../../../../../../app/shared/models/job-data.model';
import { JOB_STATUSES_ACTIVE } from '../../../../../shared/config/constants';
import { ProxyEndPoints } from '../../../../../../app/shared/config/endpoints';

@Injectable({
  providedIn: 'root',
})
export class VendorOrdersService {
  private readonly http = inject(HttpClient);
  private cachedOrders: VendorOrderSummary[] | null = null;

  private static readonly AWAITING_VENDOR_APPROVAL = 'AWAITING_VENDOR_APPROVAL';
  private static readonly AWAITING_VENDOR_SELECTION =
    'AWAITING_VENDOR_SELECTION';

  private typeCompatibleOrder(order: VendorOrderSummary | VendorOrder): {
    jobStatus?: string;
    estimates?: Array<{ quote?: { acceptedBy?: string; acceptedDate?: string } }>;
  } {
    return order as {
      jobStatus?: string;
      estimates?: Array<{ quote?: { acceptedBy?: string; acceptedDate?: string } }>;
    };
  }

  getVendorOrders(
    showVideosOnly = false,
    forceRefresh = false,
  ): Observable<VendorOrderSummary[]> {
    if (!showVideosOnly && this.cachedOrders && !forceRefresh) {
      return of(this.cachedOrders);
    }

    return this.http
      .get<
        VendorOrderSummary[]
      >(`${config.API_ENDPOINT}${ProxyEndPoints.vendorOrdersNoAcknowledge}?showVideosOnly=${showVideosOnly}`)
      .pipe(
        map((orders) => this.normalizeOrders(orders)),
        tap((orders) => {
          if (!showVideosOnly) {
            this.cachedOrders = orders ?? [];
          }
        }),
      );
  }

  getVendorOrderByUid(
    jobUid: string,
    forceRefresh = false,
  ): Observable<VendorOrderSummary | undefined> {
    if (this.cachedOrders && !forceRefresh) {
      const order = this.cachedOrders.find((order) => order.jobUid === jobUid);

      if (
        order &&
        ['IN_PROGRESS', 'COMPLETED', 'CANCELLED'].includes(
          order.jobStatus ?? '',
        )
      ) {
        const vName = this.vendorName(order.estimates);
        return this.getJobDetail(jobUid, vName, order.jobStatus);
      }

      return of(order);
    }

    return this.getVendorOrders(false, forceRefresh).pipe(
      map((orders) => orders.find((order) => order.jobUid === jobUid)),
    );
  }

  vendorName(estimates: EstimateSummary[] | undefined): string {
    if (!estimates || estimates.length === 0) {
      return '--';
    }

    const acceptedEstimate = estimates.find(
      (estimate: EstimateSummary) =>
        !!estimate.jobStatus &&
        JOB_STATUSES_ACTIVE.includes(
          estimate.jobStatus as (typeof JOB_STATUSES_ACTIVE)[number],
        ),
    );

    return acceptedEstimate?.name ?? '--';
  }

  private normalizeOrders(
    orders: VendorOrderSummary[] | null | undefined,
  ): VendorOrderSummary[] {
    if (!orders) {
      return [];
    }

    return orders.map((order) => this.normalizeOrderStatus(order));
  }

  private normalizeOrderStatus<T extends VendorOrderSummary | VendorOrder>(
    order: T,
  ): T {
    if (!this.shouldMapToAwaitingVendorSelection(this.typeCompatibleOrder(order))) {
      return order;
    }

    return {
      ...order,
      jobStatus: VendorOrdersService.AWAITING_VENDOR_SELECTION,
    };
  }

  private shouldMapToAwaitingVendorSelection(order: {
    jobStatus?: string;
    estimates?: Array<{ quote?: { acceptedBy?: string; acceptedDate?: string } }>;
  }): boolean {
    if (order.jobStatus !== VendorOrdersService.AWAITING_VENDOR_APPROVAL) {
      return false;
    }

    return (order.estimates ?? []).some((estimate) => {
      const acceptedBy = estimate.quote?.acceptedBy?.trim();
      const acceptedDate = estimate.quote?.acceptedDate?.trim();

      return !!acceptedBy && !!acceptedDate;
    });
  }

  sendMessage(request: SendMessageRequest): Promise<unknown> {
    const obs = this.http.post(
      `${config.API_ENDPOINT}${ProxyEndPoints.vendorOrdersSendMessage}`,
      request,
    );
    return lastValueFrom(obs);
  }

  async markMessagesAsRead(
    messagesId: string,
    admin: boolean,
    jobUid: string,
  ): Promise<unknown> {
    const result = await lastValueFrom(
      this.http.post(
        `${config.API_ENDPOINT}${ProxyEndPoints.vendorOrdersMarkMessagesAsRead}`,
        { messagesId, admin },
      ),
    );
    if (this.cachedOrders) {
      this.cachedOrders = this.cachedOrders.map((order) =>
        order.jobUid === jobUid ? { ...order, newMessage: false } : order,
      );
    }
    return result;
  }

  getJobDetail(
    jobUid: string,
    vendorName?: string,
    jobStatus?: string,
  ): Observable<VendorOrder | undefined> {
    let params = new HttpParams().set('jobUid', jobUid);
    if (vendorName && vendorName !== '--') {
      params = params.set('vendorName', vendorName);
    }
    if (jobStatus) {
      params = params.set('jobStatus', jobStatus);
    }
    return this.http
      .get<VendorOrder[]>(`${config.API_ENDPOINT}${ProxyEndPoints.vendorOrdersJobDetail}`, { params })
      .pipe(
        map((orders) => {
          const order = orders[0];
          return order ? this.normalizeOrderStatus(order) : undefined;
        }),
      );
  }

  getCancelledEstimations(jobUid: string): Observable<Estimate[]> {
    const params = new HttpParams().set('jobUid', jobUid);
    return this.http
      .get<{ estimates: Estimate[] }>(
        `${config.API_ENDPOINT}${ProxyEndPoints.vendorOrdersCancelledEstimations}`,
        { params },
      )
      .pipe(map((res) => res.estimates ?? []));
  }

  markJobAsCompleted(jobUid: string): Promise<{ success: boolean }> {
    const obs = this.http.post<{ success: boolean }>(
      `${config.API_ENDPOINT}/api/vendor-orders/mark-completed`,
      { jobUid },
    );
    return lastValueFrom(obs);
  }
}
